import { useDebounce } from '@uidotdev/usehooks';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { OcrItem, useAttentionMap, useCanvasOverlay, useDrawBoxes, useFilterOcrItems, useSelectionBox } from '@/hooks';
import { useDocumentStore, useValidationStore } from '@/store';
import { attentionMapToBlocks, Box } from '@/utils';

import { OcrPopover } from './OcrPopover';

type UseLoadImageOpts = {
  blob?: Blob | null;
  rotation: number;
  zoom: number;
  parentHeight?: number | null;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
};

const useLoadImage = ({ blob, rotation, zoom, parentHeight, canvasRef }: UseLoadImageOpts) => {
  const [canvasVersion, setCanvasVersion] = useState(0);

  useEffect(() => {
    if (!blob || !parentHeight) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    let frameId: number | null = null;
    let imageSrc: string | null = null;

    if (frameId != null) cancelAnimationFrame(frameId);

    frameId = requestAnimationFrame(() => {
      const img = new Image();

      img.onload = () => {
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;

        const scale = (parentHeight / img.height) * zoom;
        const radians = (rotation * Math.PI) / 180;

        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;

        canvas.width = rotation % 180 === 0 ? scaledWidth : scaledHeight;
        canvas.height = rotation % 180 === 0 ? scaledHeight : scaledWidth;

        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(radians);

        ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        ctx.restore();

        setCanvasVersion((v) => v + 1);
      };

      imageSrc = URL.createObjectURL(blob);
      img.src = imageSrc;
    });

    return () => {
      if (frameId != null) cancelAnimationFrame(frameId);
      if (imageSrc) URL.revokeObjectURL(imageSrc);
    };
  }, [blob, canvasRef, parentHeight, rotation, zoom]);

  return { canvasVersion };
};

const IImageViewer = () => {
  const blob = useDocumentStore((state) => state.documentBlob);
  const zoom = useDocumentStore((state) => state.zoom);
  const rotation = useDocumentStore((state) => state.rotation);
  const parentHeight = useValidationStore((state) => state.parentContainerHeight);

  const focusedField = useDocumentStore((state) => state.focusedField);
  const attentionMap = useMemo(() => attentionMapToBlocks(focusedField?.attentionMap), [focusedField?.attentionMap]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { canvasVersion } = useLoadImage({ blob, rotation, zoom, parentHeight, canvasRef });

  const { overlayRef: attentionMapCanvasRef, layerVersion: attentionMapLayerVersion } = useCanvasOverlay({
    canvasRef,
    canvasVersion,
  });
  useAttentionMap(attentionMapCanvasRef, attentionMap, rotation, attentionMapLayerVersion);

  const { overlayRef: ocrLayerCanvasRef, layerVersion: ocrLayerVersion } = useCanvasOverlay({
    canvasRef,
    canvasVersion,
  });
  const [ocrItems, setOcrItems] = useState<OcrItem[]>([]);
  useDrawBoxes(ocrLayerCanvasRef, ocrItems, rotation, ocrLayerVersion);

  const { overlayRef: selectionCanvasRef, layerVersion: selectionLayerVersion } = useCanvasOverlay({
    canvasRef,
    canvasVersion,
  });
  const [selectionBox, setSelectionBox] = useState<Box | undefined>();
  const debouncedSelectionBox = useDebounce(selectionBox, 50);

  const { handleMouseDown, handleMouseMove, handleMouseUp, clear } = useSelectionBox({
    canvasRef: selectionCanvasRef,
    onSelectMove: (box) => setSelectionBox(box),
    onSelectEnd: (box) => setSelectionBox(box),
    onSelectStart: (box) => setSelectionBox(box),
    canvasVersion: selectionLayerVersion,
  });

  useEffect(() => {
    if (!selectionLayerVersion) return;

    const canvas = selectionCanvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp, selectionCanvasRef, selectionLayerVersion]);

  const {
    items: filteredOcrItems,
    anchor: ocrAnchor,
    text: ocrText,
  } = useFilterOcrItems({
    selectionBox: debouncedSelectionBox,
    canvasRef,
    rotation,
    canvasVersion: selectionLayerVersion,
  });

  useEffect(() => setOcrItems(filteredOcrItems), [filteredOcrItems]);
  const dismissOcrPopover = useCallback(() => {
    setSelectionBox(undefined);
    clear();
  }, [clear]);

  return (
    <div className="flex w-full overflow-auto">
      <div className="relative left-1/2 -translate-x-1/2">
        <canvas ref={canvasRef} />
        {ocrAnchor && ocrText ? <OcrPopover anchor={ocrAnchor} text={ocrText} onDismiss={dismissOcrPopover} /> : null}
      </div>
    </div>
  );
};

export const ImageViewer = memo(IImageViewer);
